import { useContext } from 'react'
import { useGoogleLogin } from '@react-oauth/google'
import { captureEvent } from '@sentry/react'
import { useNavigate } from 'react-router-dom'

//constants
import { ERRORS, REACT_NATIVE_STATUS } from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
//services
import { connectGmail } from 'services/authService'
import { getUserById } from 'services/userService'
//utils
import { handleError, sendRNMessage } from 'utils/utils'

const useConnectGmail = (hideModal: () => void, isSignUp?: boolean) => {
  const { user, updateUserData } = useContext(AuthContext)
  const { showLoader, hideLoader } = useContext(LoaderContext)
  const navigate = useNavigate()

  const handleAuthToken = async (code: string) => {
    try {
      if (!user) {
        return
      }

      showLoader()
      hideModal()
      await connectGmail({
        authorizationCode: code,
        userId: user.id,
        redirectUri: `postmessage`,
      })
      const data = await getUserById(user?.id)
      updateUserData(data.data)
      hideLoader()
    } catch (err: any) {
      hideModal()
      if (err.response?.data.message === 'Email is already connected to another account!') {
        handleError(err, err.response.data.message)
        hideLoader()
        return
      }

      const silentErrorMessages = ['No authorization code received']

      if (err instanceof Error && !silentErrorMessages.includes(err.message)) {
        handleError(err)
        captureEvent({
          message: ERRORS.CONNECT_GMAIL,
          level: 'error',
          extra: { userId: user?.id },
        })
      }
      hideLoader()
    }
  }

  const connectGmailNative = () => {
    sendRNMessage({
      status: REACT_NATIVE_STATUS.CONNECT_SECOND,
      userId: user?.id,
      shouldNavigateToSettings: false,
    })
  }

  const connectGmailWeb = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      await handleAuthToken(tokenResponse.code)
      if (isSignUp) {
        navigate('/platform-select')
      }
    },
    onError: () => new Error('No authorization code received'),
    scope: 'https://www.googleapis.com/auth/gmail.readonly',
    flow: 'auth-code',
  })

  return { requestGmailAccess: window.ReactNativeWebView ? connectGmailNative : connectGmailWeb }
}

export default useConnectGmail
